/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  TabPanel,
  TabPanels,
  Tabs,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  unreadMessageSelector,
} from "store/project/selector";
import { searchQuerySelector } from "store/ui/selector";
import { userSelector } from "store/user/selector";
import CustomerProjectsTable from "./table";
import ProjectsHeader from "./components/ProjectsHeader";
import { ProjectsGridView } from "./components/ProjectsGridView";
import { EmptyState } from "./components/EmptyState";
import { useWindowWidth } from "@react-hook/window-size";
import CustomerPaginations from "components/pagination/CustomerPaginations";
import { axiosService } from "network/axios";
import {
  fetchUnreadMessagesByStatus,
  generateQueryParameters,
} from "network/helper";
import { getUnreadMessagesRequest } from "store/project/reducer";
import PageLayout from "components/layout/PageLayout";
import { clearDraftStorage } from "views/customer/order/utils/draftStorage";

type ViewMode = "list" | "grid";

export default function CustomerProjects() {
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchQuery = useSelector(searchQuerySelector);
  const [prevSearchQuery, setPrevSearchQuery] = useState(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const effectiveViewMode = isMobile ? "grid" : viewMode;
  const unreadMessages = useSelector(unreadMessageSelector);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const windowWidth = useWindowWidth();
  const [tabIndex, setTabIndex] = useState(0);
  const [projectCounts, setProjectCounts] = useState({
    activeProjects: 0,
    completedProjects: 0,
    draftProjects: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(20);
  const [pageLimit, setPageLimit] = useState(10);
  const [isFetching, setIsFetching] = useState(false);
  const [results, setResults] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getUnreadMessagesRequest({ user_id: Number(user.user_id) }));
    }, 60000*10); // 10 seconds = 10000 ms

    return () => clearInterval(interval); // Cleanup on unmount
  }, [dispatch, user.user_id]);

  useEffect(() => {
    if (window && user) {
      const tabs = ["Active", "Completed", "Drafts"];
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "project_status_previewed",
        user_id: user.user_id,
        project_status: tabs[tabIndex],
      });
    }
  }, [tabIndex]);


  // Fetch unread messages for other purposes
  fetchUnreadMessagesByStatus(unreadMessages);
  const status =
    tabIndex === 0
      ? "active"
      : tabIndex === 1
      ? "completed"
      : "drafts";

  const showDetailsProductDetails = (orderId: string) => {
    console.log("Showing details for order:", orderId, "with status:", status);
    if(status === "active" || status === "completed") {
      navigate(`/customer/project/${orderId}`);
    }else {
      localStorage.removeItem("uploadProject");
      localStorage.removeItem("orderStep");
      navigate(`/customer/main/${orderId}`);

    }
  };

  const queryParam = useMemo(() => {
    if (prevSearchQuery !== searchQuery) {
      setPrevSearchQuery(searchQuery);
      setCurrentPage(1);
      return {
        status: status,
        page: 1,
        limit: pageLimit,
        keyword: searchQuery ?? "",
        order_by: "updated_at",
        dir: "desc",
      };
    }
    return {
      status: status,
      page: currentPage,
      limit: pageLimit,
      keyword: searchQuery ?? "",
      order_by: "updated_at",
      dir: "desc",
    };
  }, [tabIndex, currentPage, pageLimit, searchQuery]);

  const fetchResults = useCallback(async () => {
    setIsFetching(true);
    const params = generateQueryParameters(queryParam);
    try {
      const resp: any = await axiosService.get(
        `/projects/list/${user?.user_id}?${params}`
      );
      const { results, total_rows } = resp?.data;
      setResults(
        results?.map((it: any) => ({
          ...it,
          id: it?.order_id,
        }))
      );

      setTotalPages(
        total_rows / pageLimit > Math.floor(total_rows / pageLimit)
          ? Math.floor(total_rows / pageLimit) + 1
          : Math.floor(total_rows / pageLimit)
      );
      setTotalRows(total_rows);
    } catch (err) {
      console.log(err);
      setResults([]);
    } finally {
      setIsFetching(false);
    }
  }, [user?.user_id, queryParam]);

  useEffect(() => {
    if (user?.user_id) {
      fetchResults();
    }
  }, [user?.user_id, queryParam]);

  const fetchProjectCounts = useCallback(async () => {
    try {
      const { data }: any = await axiosService.get(
        `/projects/customer/summary/${user?.user_id}`
      );
      setProjectCounts({
        activeProjects: data?.activeProjects ?? 0,
        completedProjects: data?.completedProjects ?? 0,
        draftProjects: data?.draftProjects ?? 0,
      });
    } catch (err) {
      console.log(err);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (user?.user_id) {
      fetchProjectCounts();
    }
  }, [user?.user_id, fetchProjectCounts]);

  const handleStartNewOrder = () => {
    // Clear previous draft data before starting new order
    clearDraftStorage(user?.user_id);
    window.location.href = "/customer/main";
  };

  return (
    <PageLayout title="Projects">
      <ProjectsHeader
        tabIndex={tabIndex}
        onTabChange={(index) => {
          setCurrentPage(1);
          setTabIndex(index);
        }}
        activeMessagesCnt={projectCounts.activeProjects}
        completeMessagesCnt={projectCounts.completedProjects}
        draftsProjectsCnt={projectCounts.draftProjects}
        onStartNewOrder={handleStartNewOrder}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isMobile={isMobile}
      />
      <Tabs
        variant="unstyled"
        index={tabIndex}
        onChange={(index) => {
          setCurrentPage(1);
          setTabIndex(index);
        }}
      >

        <TabPanels>
          <TabPanel px="0" py="0">
            {(isFetching || results?.length > 0) &&
              (effectiveViewMode === "list" ? (
                <CustomerProjectsTable
                  pageLimit={pageLimit}
                  isFetching={isFetching}
                  tableData={results}
                  onShowCustomerProjectDetails={showDetailsProductDetails}
                  tabIndex={tabIndex}
                />
              ) : (
                <ProjectsGridView
                  projects={results}
                  isFetching={isFetching}
                  onViewDetails={showDetailsProductDetails}
                  pageLimit={pageLimit}
                  tabIndex={tabIndex}
                />
              ))}
            {!isFetching && results?.length < 1 && (
              <EmptyState onStartOrder={handleStartNewOrder} />
            )}
          </TabPanel>

          <TabPanel px="0" py="0">
            {(isFetching || results?.length > 0) &&
              (effectiveViewMode === "list" ? (
                <CustomerProjectsTable
                  pageLimit={pageLimit}
                  isFetching={isFetching}
                  tableData={results}
                  onShowCustomerProjectDetails={showDetailsProductDetails}
                  tabIndex={tabIndex}
                />
              ) : (
                <ProjectsGridView
                  projects={results}
                  isFetching={isFetching}
                  onViewDetails={showDetailsProductDetails}
                  pageLimit={pageLimit}
                  tabIndex={tabIndex}
                />
              ))}
            {!isFetching && results?.length < 1 && (
              <EmptyState onStartOrder={handleStartNewOrder} />
            )}
          </TabPanel>
          <TabPanel px="0" py="0">
            {(isFetching || results?.length > 0) &&
              (effectiveViewMode === "list" ? (
                <CustomerProjectsTable
                  pageLimit={pageLimit}
                  isFetching={isFetching}
                  tableData={results}
                  onShowCustomerProjectDetails={showDetailsProductDetails}
                  onRefresh={fetchResults}
                  tabIndex={tabIndex}
                />
              ) : (
                <ProjectsGridView
                  projects={results}
                  isFetching={isFetching}
                  onViewDetails={showDetailsProductDetails}
                  pageLimit={pageLimit}
                  tabIndex={tabIndex}
                />
              ))}
            {!isFetching && results?.length < 1 && (
              <EmptyState onStartOrder={handleStartNewOrder} />
            )}
          </TabPanel>
        </TabPanels>
        {!isFetching && totalPages > 1 && (
          <Box mt="24px">
            <CustomerPaginations
            totalCount={totalRows}
            totalPages={totalPages}
            currentPage={currentPage}
            perPage={pageLimit}
            showPageSizeSelector={false}
            onSetPageLimit={(e: number) => {
              setPageLimit(e);
              setCurrentPage(1);
            }}
            onChangePage={(e: number) => {
              setCurrentPage(e);
            }}
          />
        </Box>
        )}
      </Tabs>
    </PageLayout>
  );
}
